// Agent-side Socket.IO connection for the ecom live chat.
// Connects as role=ecom_agent (authenticated with the tenant JWT).
//
// REAL-TIME ONLY: this socket handles live messaging, typing indicators, chat
// actions (accept/close/transfer), and voice call signaling. All data fetching
// (sessions, threads, customers, shoppers) is handled via REST APIs in ecomChatApi.js.

import { io } from "socket.io-client";

let socket = null;

function socketUrl() {
  if (process.env.REACT_APP_SOCKET_URL) return process.env.REACT_APP_SOCKET_URL;
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${window.location.host}`;
}

export function getAgentSocket() {
  return socket;
}

export function connectAgentSocket(token) {
  if (socket && socket.connected) return socket;
  if (socket) {
    try {
      socket.disconnect();
    } catch (e) {
      /* noop */
    }
    socket = null;
  }
  socket = io(socketUrl(), {
    path: "/socket.io",
    transports: ["websocket"],
    query: { role: "ecom_agent", token },
    reconnection: true,
    reconnectionDelay: 2000,
    reconnectionDelayMax: 15000,
    timeout: 20000,
  });
  return socket;
}

// ---- chat actions (real-time mutations) ----

export function acceptChat(sessionId) {
  if (!socket) return;
  const doAccept = () => socket.emit("ecom_chat_accept", { session_id: sessionId });
  if (socket.connected) { doAccept(); return; }
  socket.once("connect", doAccept);
}

export function sendAgentMessage(sessionId, payload) {
  if (socket && socket.connected) {
    socket.emit("ecom_chat_message", { session_id: sessionId, ...(payload || {}) });
  }
}

export function closeChat(sessionId) {
  if (socket && socket.connected) socket.emit("ecom_chat_close", { session_id: sessionId });
}

export function initiateChat(visitorId, body) {
  if (socket && socket.connected) socket.emit("ecom_chat_initiate", { visitor_id: visitorId, body });
}

export function initiateChatWithCustomer(customerId, body) {
  if (socket && socket.connected) socket.emit("ecom_chat_initiate", { customer_id: customerId, body });
}

export function transferChat(sessionId, targetAgentId, note, force) {
  if (socket && socket.connected) {
    socket.emit("ecom_chat_transfer", {
      session_id: sessionId,
      target_agent_id: targetAgentId,
      note,
      force: !!force,
    });
  }
}

export function releaseChat(sessionId) {
  if (socket && socket.connected) socket.emit("ecom_chat_release", { session_id: sessionId });
}

export function sendAgentTyping(visitorId, typing) {
  if (socket && socket.connected) socket.emit("ecom_typing", { visitor_id: visitorId, typing: !!typing });
}

// ---- voice call (WebRTC signaling) ----

export function startCall(sessionId) {
  if (socket && socket.connected) socket.emit("ecom_call_start", { session_id: sessionId });
}

export function sendCallOffer(sessionId, callId, sdp) {
  if (socket && socket.connected) socket.emit("ecom_call_offer", { session_id: sessionId, call_id: callId, sdp });
}

export function sendCallIce(sessionId, callId, candidate) {
  if (socket && socket.connected) socket.emit("ecom_call_ice", { session_id: sessionId, call_id: callId, candidate });
}

export function notifyCallConnected(sessionId, callId) {
  if (socket && socket.connected) socket.emit("ecom_call_connected", { session_id: sessionId, call_id: callId });
}

export function endCall(sessionId, callId, reason) {
  if (socket && socket.connected) socket.emit("ecom_call_end", { session_id: sessionId, call_id: callId, reason });
}

export function disconnectAgentSocket() {
  if (socket) {
    try {
      socket.disconnect();
    } catch (e) {
      /* noop */
    }
    socket = null;
  }
}
